import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Input, Button, Table } from 'reactstrap';
import apiClient from '../../api';

/**
 * Manage Roles — migrated from the bespoke theme.js styling (slate
 * palette + plain HTML <table> + inline SVG icons) to Velzon-standard
 * reactstrap components.
 *
 * Business logic is UNCHANGED:
 *   - GET    /api/role                    → fetch role list
 *   - POST   /api/role  { roleName }      → add a role
 *   - DELETE /api/role/:roleId            → remove a role
 *
 * Every state name, every handler name, every API path, every payload
 * field is byte-identical to the pre-migration version.
 */
const ManageRoles = () => {
    const [roles, setRoles] = useState([]);
    const [newRoleName, setNewRoleName] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState('');

    const fetchRoles = async () => {
        try {
            const res = await apiClient.get('role');
            const list = res?.data;
            setRoles(Array.isArray(list) ? list : []);
        } catch (e) { console.error(e); }
    };

    const handleAdd = async () => {
        if (!newRoleName.trim()) return alert('Please enter a role name.');
        try {
            await apiClient.post('role', { roleName: newRoleName });
            setNewRoleName('');
            fetchRoles();
        } catch (e) { console.error(e); }
    };

    const handleDelete = async () => {
        if (!selectedRoleId) return alert('Please select a role to delete.');
        try {
            await apiClient.delete(`role/${selectedRoleId}`);
            setSelectedRoleId('');
            fetchRoles();
        } catch (e) { console.error(e); }
    };

    useEffect(() => { fetchRoles(); }, []);

    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0 pt-3 px-3 pb-2">
                <div className="d-flex align-items-center gap-2">
                    <span
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: "#eef1fa", color: "#405189", flexShrink: 0,
                        }}
                        aria-hidden="true"
                    >
                        <i className="ri-shield-user-line" style={{ fontSize: 16 }} />
                    </span>
                    <div>
                        <h6 className="mb-0 fw-semibold">Manage roles</h6>
                        <p className="text-muted small mb-0">Add or remove system roles</p>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="pt-3">
                {/* ── Add role ── */}
                <div className="text-uppercase fw-semibold text-muted mb-2"
                    style={{ fontSize: 11, letterSpacing: '0.04em' }}>
                    Add role
                </div>
                <Row className="g-2 align-items-center mb-3">
                    <Col xs="auto" style={{ minWidth: 260 }}>
                        <Input
                            bsSize="sm"
                            placeholder="Enter new role name"
                            value={newRoleName}
                            onChange={e => setNewRoleName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        />
                    </Col>
                    <Col xs="auto">
                        <Button color="primary" size="sm" onClick={handleAdd}>
                            <i className="ri-add-line align-bottom me-1" />
                            Add role
                        </Button>
                    </Col>
                </Row>

                {/* ── Delete role ── */}
                <div className="text-uppercase fw-semibold text-muted mb-2"
                    style={{ fontSize: 11, letterSpacing: '0.04em' }}>
                    Delete role
                </div>
                <Row className="g-2 align-items-center mb-3">
                    <Col xs="auto" style={{ minWidth: 260 }}>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={selectedRoleId}
                            onChange={e => setSelectedRoleId(Number(e.target.value))}
                        >
                            <option value="">Select role to delete</option>
                            {roles.map(r => (
                                <option key={r.roleId} value={r.roleId}>{r.roleName}</option>
                            ))}
                        </Input>
                    </Col>
                    <Col xs="auto">
                        <Button color="danger" size="sm" onClick={handleDelete}>
                            <i className="ri-delete-bin-line align-bottom me-1" />
                            Delete
                        </Button>
                    </Col>
                </Row>

                {/* ── All roles table ── */}
                <div className="text-uppercase fw-semibold text-muted mb-2"
                    style={{ fontSize: 11, letterSpacing: '0.04em' }}>
                    All roles
                </div>
                <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0" hover>
                        <thead className="table-light">
                            <tr>
                                <th>ID</th>
                                <th>Role name</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roles.length > 0 ? roles.map((r) => (
                                <tr key={r.roleId}>
                                    <td>{r.roleId}</td>
                                    <td>{r.roleName}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={2} className="text-center text-muted py-3">
                                        No roles found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </CardBody>
        </Card>
    );
};

export default ManageRoles;
